import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { MenuList, Box, CircularProgress } from '@mui/material';
import React, { FC, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import getAnswersAsCsv from '../../../api/getAnswers';
import getChatConversation from '../../../api/getChatConversation';
import getHotAlertAnswers from '../../../api/getHotAlertAnswers';
import { useLanguage } from '../../../contexts/languageContext';
import { TenantContext } from '../../../contexts/tenantContext';
import { colors, fontWeight, TypographyStyledGlobal } from '../../../theme';
import {
    ArrowDropUpIconStyled,
    ArrowDropDownIconStyled,
    MenuItemsStyled,
    DividerMenuStyled,
} from '../../../theme/style';
import ExportFormat from '../../../utils/enum/ExportFormat';
import ExportType from '../../../utils/enum/ExportType';
import { ChatingProps } from '../../Modals/DashboardModal/dashboardModalInterface';
import ExportGdprModal from '../../Modals/ExportGdprModal/ExportGdprModal';
import {
    ClauseFilterProps,
    DateRangeScopeProps,
    ScopeProps,
} from '../dashboardInterfaces';
import { ButtonDropdownStyled, MenuDropdownStyled } from './styled';

interface ExportDropdownProps {
    exportPdf: boolean
    selectedClauseFilter: Array<ClauseFilterProps>
    selectedScope: Array<ScopeProps>
    selectedDateRange: DateRangeScopeProps | null
    surveyId: string
    exportCase: ExportType
    conversation?: Array<ChatingProps>
    filterClauseLabel?: string
    scopeClauseLabel?: string
    exportTxt?: boolean
    exportWord?: boolean
    vertical: string
    horizontal: string
    summary?: string
    disabled?: boolean
    questionLabel?: string
    dashboardName?: string
    surveyName?: string
}

const ExportDropdown: FC<ExportDropdownProps> = ({ exportPdf, selectedClauseFilter, selectedScope, selectedDateRange, surveyId, exportCase, conversation, filterClauseLabel, scopeClauseLabel, exportTxt, exportWord, vertical, horizontal, summary, disabled, questionLabel, dashboardName, surveyName }) => {
    const [openDropdownMenu, setOpenDropdownMenu] = useState<boolean>(false);
    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const { t } = useTranslation('dashboard');
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const clauseFilters = JSON.stringify(selectedClauseFilter.map((clauseFilter: ClauseFilterProps) => ({ [clauseFilter.filterId]: clauseFilter.modalityId.map((modalityId: string) => parseInt(modalityId, 10)) })));
    const selectedScopes = JSON.stringify(selectedScope.map((scope: ScopeProps) => ({ [scope.scopeId]: scope.modalityId.map((modalityId: string) => parseInt(modalityId, 10)) })));
    const selectedDateRanges = JSON.stringify({ startDate: selectedDateRange?.startDate.toLocaleDateString('fr-CA'), endDate: selectedDateRange?.endDate.toLocaleDateString('fr-CA'), key: selectedDateRange?.key });
    const { selectedLanguage } = useLanguage();
    const selectedLanguageForSurvey = selectedLanguage && selectedLanguage.surveysLanguagesIds.find((surveyLanguage) => String(surveyLanguage.surveyId) === surveyId);
    const filterScopeArray = [selectedClauseFilter.length > 0 ? `filters=${clauseFilters}` : '', selectedScope.length > 0 ? `scopes=${selectedScopes}` : '', selectedDateRange ? `dateRanges=${selectedDateRanges}` : '', (selectedLanguageForSurvey && !selectedLanguageForSurvey.isDefault) ? `translate=${selectedLanguageForSurvey.languageId}` : ''].filter((fs: string) => fs !== '');
    const filterScopeLink = filterScopeArray.length > 0 ? `/?${filterScopeArray.join('&')}` : '';
    const [openGdprModal, setOpenGdprModal] = useState<boolean>(false);

    const handleChangeStatusMenu = (event: React.MouseEvent<HTMLElement>) => {
        setOpenDropdownMenu(!openDropdownMenu);
        setAnchorElNav(event.currentTarget);
    };

    const handleCsvExport = async () => {
        setIsExporting(true);
        if (exportCase === ExportType.ANSWERS) {
            try {
                await getAnswersAsCsv(surveyId, filterScopeLink, selectedSchema || '');
            } catch (error) {
                console.error('CSV export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
        if (exportCase === ExportType.HOT_ALERTS) {
            try {
                await getHotAlertAnswers(surveyId, filterScopeLink, selectedSchema || '');
            } catch (error) {
                console.error('CSV export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
        if (exportCase === ExportType.CHAT) {
            try {
                if (conversation) {
                    await getChatConversation(surveyId, questionLabel || '', scopeClauseLabel || t('filter.noFilter'), filterClauseLabel || t('filter.noFilter'), conversation, summary, ExportFormat.CSV, surveyName, dashboardName, selectedSchema || '');
                }
            } catch (error) {
                console.error('CSV export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
    };

    const handleTxtExport = async () => {
        setIsExporting(true);
        if (exportCase === ExportType.ANSWERS) {
            try {
                console.info('EXPORT TXT TO IMPLEMENT');
            } catch (error) {
                console.error('TXT export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
        if (exportCase === ExportType.HOT_ALERTS) {
            try {
                console.info('EXPORT TXT TO IMPLEMENT');
            } catch (error) {
                console.error('TXT export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
        if (exportCase === ExportType.CHAT) {
            try {
                if (conversation) {
                    await getChatConversation(surveyId, questionLabel || '', scopeClauseLabel || t('filter.noFilter'), filterClauseLabel || t('filter.noFilter'), conversation, summary, ExportFormat.TXT, surveyName, dashboardName, selectedSchema || '');
                }
            } catch (error) {
                console.error('TXT export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
    };

    const handleWordExport = async () => {
        setIsExporting(true);
        if (exportCase === ExportType.ANSWERS) {
            try {
                console.info('EXPORT WORD TO IMPLEMENT');
            } catch (error) {
                console.error('WORD export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
        if (exportCase === ExportType.HOT_ALERTS) {
            try {
                console.info('EXPORT WORD TO IMPLEMENT');
            } catch (error) {
                console.error('WORD export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
        if (exportCase === ExportType.CHAT) {
            try {
                if (conversation) {
                    await getChatConversation(surveyId, questionLabel || '', scopeClauseLabel || t('filter.noFilter'), filterClauseLabel || t('filter.noFilter'), conversation, summary, ExportFormat.WORD, surveyName, dashboardName, selectedSchema || '');
                }
            } catch (error) {
                console.error('WORD export failed:', error);
            } finally {
                setIsExporting(false);
            }
        }
    };

    const handleCloseGdprModal = () => {
        setOpenGdprModal(false);
    };

    const handleExport = (exportFormat: ExportFormat) => {
        if (exportCase === ExportType.ANSWERS) {
            switch (exportFormat) {
                case ExportFormat.CSV:
                    handleCsvExport();
                    break;
                case ExportFormat.TXT:
                    break;
                default:
                    return undefined;
            }
        }
        if (exportCase === ExportType.HOT_ALERTS) {
            setOpenGdprModal(true);
        }
        if (exportCase === ExportType.CHAT) {
            switch (exportFormat) {
                case ExportFormat.CSV:
                    handleCsvExport();
                    break;
                case ExportFormat.TXT:
                    handleTxtExport();
                    break;
                case ExportFormat.WORD:
                    handleWordExport();
                    break;
                default:
                    return undefined;
            }
        }

        return undefined;
    };

    return (
        <ButtonDropdownStyled
            disabled={disabled}
            endIcon={openDropdownMenu ? <ArrowDropUpIconStyled /> : <ArrowDropDownIconStyled />}
            menuopen={openDropdownMenu ? 1 : 0}
            onClick={handleChangeStatusMenu}
            disableRipple
        >
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                <DownloadOutlinedIcon sx={{ width: '1em', heigh: '1em', fontSize: '20px' }} />
                <TypographyStyledGlobal
                    color={colors.default}
                    margin="auto 8px auto 8px"
                    weight='500'
                >
                    {t('exportDashboard')}
                </TypographyStyledGlobal>
                {isExporting && (
                    <CircularProgress
                        size={16}
                        sx={{
                            marginLeft: '0px',
                            marginRight: '0px',
                            color: colors.default,
                        }}
                    />
                )}
            </Box>

            <MenuDropdownStyled
                anchorEl={anchorElNav}
                anchorOrigin={{ vertical, horizontal }}
                open={openDropdownMenu}
            >
                <MenuList sx={{ padding: 0, width: '100%' }}>
                    {exportPdf && (
                        <MenuItemsStyled key="export-pdf" onClick={() => { setOpenDropdownMenu(false); setAnchorElNav(null); setTimeout(() => window.print(), 150); }} disableRipple>
                            <TypographyStyledGlobal
                                textAlign="center"
                                weight={fontWeight.semiRegular}
                                width='100%'
                            >
                                {t('exportDashboardPdf')}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                    )}
                    <MenuItemsStyled
                        key="export-csv"
                        disabled={isExporting}
                        onClick={() => handleExport(ExportFormat.CSV)}
                        disableRipple
                    >
                        <TypographyStyledGlobal
                            textAlign="center"
                            weight={fontWeight.semiRegular}
                            width='100%'
                        >
                            {t('exportDashboardCsv')}
                        </TypographyStyledGlobal>
                    </MenuItemsStyled>
                    {exportTxt && (
                        <MenuItemsStyled
                            key="export-txt"
                            disabled={isExporting}
                            onClick={() => handleExport(ExportFormat.TXT)}
                            disableRipple
                        >
                            <TypographyStyledGlobal
                                textAlign="center"
                                weight={fontWeight.semiRegular}
                                width='100%'
                            >
                                {t('exportDashboardTxt')}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                    )}
                    {exportWord && (
                        <MenuItemsStyled
                            key="export-word"
                            disabled={isExporting}
                            onClick={() => handleExport(ExportFormat.WORD)}
                            disableRipple
                        >
                            <TypographyStyledGlobal
                                textAlign="center"
                                weight={fontWeight.semiRegular}
                                width='100%'
                            >
                                {t('exportDashboardWord')}
                            </TypographyStyledGlobal>
                        </MenuItemsStyled>
                    )}
                    <DividerMenuStyled sx={{ display: 'none' }} />
                </MenuList>
            </MenuDropdownStyled>
            <ExportGdprModal handleClose={handleCloseGdprModal} handleDownload={handleCsvExport} openModal={openGdprModal} title={t('exportDashboardCsv')} />
        </ButtonDropdownStyled>
    );
};

export default ExportDropdown;
